export const GLYPH_WIDTH = 3
export const GLYPH_HEIGHT = 5
/** Horizontal distance between the starts of two glyphs. */
export const GLYPH_ADVANCE = 4

/**
 * Each glyph is five rows of three pixels, written as bit strings
 * (top row first, leftmost pixel first) and packed into a 15-bit number.
 */
const GLYPH_SOURCE: Readonly<Record<string, readonly [string, string, string, string, string]>> = {
  A: ['010', '101', '111', '101', '101'],
  B: ['110', '101', '110', '101', '110'],
  C: ['011', '100', '100', '100', '011'],
  D: ['110', '101', '101', '101', '110'],
  E: ['111', '100', '110', '100', '111'],
  F: ['111', '100', '110', '100', '100'],
  G: ['011', '100', '101', '101', '011'],
  H: ['101', '101', '111', '101', '101'],
  I: ['111', '010', '010', '010', '111'],
  J: ['001', '001', '001', '101', '010'],
  K: ['101', '101', '110', '101', '101'],
  L: ['100', '100', '100', '100', '111'],
  M: ['101', '111', '111', '101', '101'],
  N: ['110', '101', '101', '101', '101'],
  O: ['111', '101', '101', '101', '111'],
  P: ['110', '101', '110', '100', '100'],
  Q: ['010', '101', '101', '110', '011'],
  R: ['110', '101', '110', '101', '101'],
  S: ['011', '100', '010', '001', '110'],
  T: ['111', '010', '010', '010', '010'],
  U: ['101', '101', '101', '101', '111'],
  V: ['101', '101', '101', '101', '010'],
  W: ['101', '101', '111', '111', '101'],
  X: ['101', '101', '010', '101', '101'],
  Y: ['101', '101', '010', '010', '010'],
  Z: ['111', '001', '010', '100', '111'],
  '0': ['010', '101', '101', '101', '010'],
  '1': ['010', '110', '010', '010', '111'],
  '2': ['110', '001', '010', '100', '111'],
  '3': ['111', '001', '011', '001', '111'],
  '4': ['101', '101', '111', '001', '001'],
  '5': ['111', '100', '110', '001', '110'],
  '6': ['011', '100', '111', '101', '111'],
  '7': ['111', '001', '010', '010', '010'],
  '8': ['111', '101', '111', '101', '111'],
  '9': ['111', '101', '111', '001', '110'],
  ' ': ['000', '000', '000', '000', '000'],
  '.': ['000', '000', '000', '000', '010'],
  ',': ['000', '000', '000', '010', '100'],
  '!': ['010', '010', '010', '000', '010'],
  '?': ['110', '001', '010', '000', '010'],
  ':': ['000', '010', '000', '010', '000'],
  ';': ['000', '010', '000', '010', '100'],
  "'": ['010', '010', '000', '000', '000'],
  '"': ['101', '101', '000', '000', '000'],
  '-': ['000', '000', '111', '000', '000'],
  '+': ['000', '010', '111', '010', '000'],
  '=': ['000', '111', '000', '111', '000'],
  '/': ['001', '001', '010', '100', '100'],
  '(': ['010', '100', '100', '100', '010'],
  ')': ['010', '001', '001', '001', '010'],
  '<': ['001', '010', '100', '010', '001'],
  '>': ['100', '010', '001', '010', '100'],
  '[': ['110', '100', '100', '100', '110'],
  ']': ['011', '001', '001', '001', '011'],
  '{': ['011', '010', '110', '010', '011'],
  '}': ['110', '010', '011', '010', '110'],
  '@': ['111', '101', '111', '100', '111'],
  '#': ['101', '111', '101', '111', '101'],
  $: ['010', '011', '010', '110', '010'],
  '%': ['101', '001', '010', '100', '101'],
  '&': ['010', '101', '010', '101', '011'],
  '*': ['101', '010', '111', '010', '101'],
  _: ['000', '000', '000', '000', '111'],
  '~': ['000', '001', '111', '100', '000'],
  '^': ['010', '101', '000', '000', '000'],
  '|': ['010', '010', '010', '010', '010'],
  '\\': ['100', '100', '010', '001', '001'],
  '`': ['100', '010', '000', '000', '000'],
}

const pack = (rows: readonly string[]): number =>
  rows.reduce((acc, row) => (acc << GLYPH_WIDTH) | parseInt(row, 2), 0)

/** Packed 15-bit glyphs, keyed by character. Lowercase letters are not keys; see glyphRows. */
export const FONT_GLYPHS: Readonly<Record<string, number>> = Object.freeze(
  Object.fromEntries(Object.entries(GLYPH_SOURCE).map(([ch, rows]) => [ch, pack(rows)])),
)

/** Every character with a glyph of its own. */
export const FONT_CHARS: readonly string[] = Object.freeze(Object.keys(FONT_GLYPHS))

const BOX = pack(['111', '101', '101', '101', '111'])
const ROW_MASK = (1 << GLYPH_WIDTH) - 1

const unpack = (glyph: number): number[] =>
  Array.from(
    { length: GLYPH_HEIGHT },
    (_, i) => (glyph >> ((GLYPH_HEIGHT - 1 - i) * GLYPH_WIDTH)) & ROW_MASK,
  )

/** Five rows of three bits for a character; unknown characters become a hollow box. */
export const glyphRows = (ch: string): number[] => {
  const key = ch.length === 1 ? ch.toUpperCase() : ''
  const glyph = FONT_GLYPHS[key]
  return unpack(glyph === undefined ? BOX : glyph)
}
